/* React hooks for API access. Keeps loading/error/refetch boilerplate
 * out of every screen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './errors.js';

/**
 * useApi(fn, deps)
 *   fn: an async function — typically a call into src/api/*.
 *   deps: dep array; refetches when these change. Pass [] to run once.
 *
 * Returns { data, error, loading, refetch }.
 * Unmount-safe: ignores state updates after unmount.
 */
export function useApi(fn, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const run = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      if (mounted.current) setState({ data, error: null, loading: false });
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError({ code: 'unknown', message: e?.message || 'failed', status: 0 });
      if (mounted.current) setState({ data: null, error: err, loading: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { run(); }, [run]);

  return { ...state, refetch: run };
}

/**
 * usePolling(fn, intervalMs, untilFn?)
 *   Polls until `untilFn(data) === true` (defaults to never stopping while mounted).
 *   Pauses while document is hidden to avoid burning battery.
 */
export function usePolling(fn, intervalMs, untilFn) {
  const [state, setState] = useState({ data: null, error: null });
  const stopRef = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let timer;
    stopRef.current = false;

    const tick = async () => {
      if (stopRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) {
        timer = setTimeout(tick, intervalMs);
        return;
      }
      try {
        const data = await fnRef.current();
        setState({ data, error: null });
        if (untilFn && untilFn(data)) { stopRef.current = true; return; }
      } catch (error) {
        setState((s) => ({ ...s, error }));
      }
      if (!stopRef.current) timer = setTimeout(tick, intervalMs);
    };

    tick();
    return () => { stopRef.current = true; if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return state;
}

/** useAction(fn) — for mutations. Returns { run, loading, error, data, reset }. */
export function useAction(fn) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const run = useCallback(async (...args) => {
    setState({ data: null, error: null, loading: true });
    try {
      const data = await fn(...args);
      if (mounted.current) setState({ data, error: null, loading: false });
      return data;
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError({ code: 'unknown', message: e?.message || 'failed', status: 0 });
      if (mounted.current) setState({ data: null, error: err, loading: false });
      throw err;
    }
  }, [fn]);

  const reset = useCallback(() => setState({ data: null, error: null, loading: false }), []);
  return { ...state, run, reset };
}
